import {Button, Modal, message, Space} from "antd";
import {FormEvent, useEffect, useState} from "react";
import OTPInput from "react-otp-input";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {
  useConfirmPasswordChange,
  useSendPasswordChangeOtp,
  useVerifyPasswordChangeOtp,
} from "../../../react-query-hooks/settings";
import {
  StyledOtpInput,
  StyledTextButton,
} from "../../payouts/v2/tabs/payout-settings-tab/otp-view/styles";
import styled, {createGlobalStyle} from "styled-components";
import {CustomInputComponent} from "../../input/input-component";
import yup from "../../../utils/yup-extended";
import {PASSWORD_CHANGE_SESSION_EXPIRED_MESSAGE} from "./password-change.constants";

const StyledPasswordOtpInput = styled(StyledOtpInput)`
  && {
    flex: 1 1 0;
    width: 100% !important;
    max-width: 6.2rem;
    min-width: 0;
    height: clamp(4rem, 12vw, 4.8rem) !important;
    border-radius: 4px;
    font-size: clamp(1.6rem, 4vw, 1.8rem);
    text-align: center;
  }

  &:focus {
    outline: none;
  }
`;

const VerifyPasswordModalGlobalStyles = createGlobalStyle`
  .verify-password-modal {
    .ant-modal-content {
      border-radius: 8px;
    }

    .ant-modal-header {
      border-bottom: 1px solid #F0F0F0;
      padding: 20px 24px;
      margin: 0;
    }

    .ant-modal-body {
      padding: 20px 24px 24px;
    }
  }
`;

type PasswordChangeStep = "otp" | "password";

interface IPasswordFormValues {
  password: string;
  confirmPassword: string;
}

const passwordSchema = yup.object().shape({
  password: yup
    .string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters"),
  confirmPassword: yup
    .string()
    .required("Please confirm your password")
    .oneOf([yup.ref("password")], "Passwords must match"),
});

interface VerifyPasswordModalProps {
  visible: boolean;
  currentPassword: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function VerifyPasswordModal({
  visible,
  currentPassword,
  onClose,
  onSuccess,
}: VerifyPasswordModalProps) {
  const [step, setStep] = useState<PasswordChangeStep>("otp");
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(15);
  const verifyOtpMutation = useVerifyPasswordChangeOtp();
  const confirmPasswordMutation = useConfirmPasswordChange();
  const sendOtpMutation = useSendPasswordChangeOtp();

  const {
    handleSubmit,
    formState: {errors},
    watch,
    control,
    reset,
  } = useForm<IPasswordFormValues>({
    resolver: yupResolver(passwordSchema),
    mode: "onChange",
  });

  const handleVerifyOtp = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (otp.length < 6) {
      message.error("Please enter the complete 6-digit OTP");
      return;
    }

    verifyOtpMutation.mutate(otp, {
      onSuccess: () => {
        setStep("password");
      },
    });
  };

  const handleConfirmPassword = (values: IPasswordFormValues) => {
    confirmPasswordMutation.mutate(
      {
        password: values.password,
        confirmPassword: values.confirmPassword,
      },
      {
        onSuccess: () => {
          setOtp("");
          setStep("otp");
          reset();
          onSuccess();
          onClose();
        },
        onError: (error: {message?: string}) => {
          if (error.message === PASSWORD_CHANGE_SESSION_EXPIRED_MESSAGE) {
            message.error(error.message);
            setStep("otp");
            setOtp("");
            reset();
            setTimer(0);
          }
        },
      }
    );
  };

  const handleResendOTP = () => {
    sendOtpMutation.mutate(currentPassword, {
      onSuccess: () => {
        setTimer(15);
        setOtp("");
      },
    });
  };

  useEffect(() => {
    if (!visible) {
      setStep("otp");
      setOtp("");
      setTimer(15);
      reset();
      return;
    }

    const timerCount = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerCount);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerCount);
  }, [visible, reset]);

  const password = watch("password");
  const confirmPassword = watch("confirmPassword");
  const isVerifyOtpDisabled = otp.length < 6 || verifyOtpMutation.isLoading;
  const isConfirmPasswordDisabled =
    !password ||
    !confirmPassword ||
    Object.keys(errors).length > 0 ||
    confirmPasswordMutation.isLoading;

  const modalTitle = step === "otp" ? "Verify password change" : "Set new password";

  const modalSubtitle =
    step === "otp"
      ? "Please enter the 6-digit OTP that has been sent to your email address"
      : "Enter your new password. You have 5 minutes from OTP verification to complete this step.";

  return (
    <>
      <VerifyPasswordModalGlobalStyles />
      <Modal
        title={modalTitle}
        open={visible}
        onCancel={onClose}
        footer={null}
        closable={true}
        centered
        width={720}
        wrapClassName="verify-password-modal"
      >
        {step === "otp" ? (
          <form onSubmit={handleVerifyOtp}>
            <div style={{width: "100%"}}>
              <p style={{fontSize: "1.6rem", margin: 0, lineHeight: "2.4rem"}}>
                {modalSubtitle}
              </p>
              <div style={{marginTop: "20px"}}>
                <OTPInput
                  value={otp}
                  onChange={setOtp}
                  numInputs={6}
                  shouldAutoFocus
                  inputType="tel"
                  containerStyle={{
                    display: "flex",
                    gap: "clamp(8px, 2vw, 16px)",
                    width: "100%",
                  }}
                  renderInput={(props) => <StyledPasswordOtpInput {...props} />}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                  marginTop: "36px",
                  minHeight: "56px",
                }}
              >
                <div>
                  {timer > 0 ? (
                    <p style={{fontSize: "2rem", fontWeight: 600, margin: 0}}>
                      {timer} secs
                    </p>
                  ) : (
                    <StyledTextButton type="text" size="small" onClick={handleResendOTP}>
                      <span className="text-underline">Resend OTP</span>
                    </StyledTextButton>
                  )}
                </div>

                <Button
                  htmlType="submit"
                  disabled={isVerifyOtpDisabled}
                  loading={verifyOtpMutation.isLoading}
                  style={{
                    borderRadius: 14,
                    ...(isVerifyOtpDisabled
                      ? {
                          backgroundColor: "#F5F5F5",
                          borderColor: "#D9D9D9",
                          color: "#BFBFBF",
                        }
                      : {
                          backgroundColor: "#1C1726",
                          borderColor: "#1C1726",
                          color: "#fff",
                        }),
                  }}
                >
                  Verify code
                </Button>
              </div>
            </div>
          </form>
        ) : (
          <form onSubmit={handleSubmit(handleConfirmPassword)}>
            <div style={{width: "100%"}}>
              <p style={{fontSize: "1.6rem", margin: 0, lineHeight: "2.4rem"}}>
                {modalSubtitle}
              </p>

              <Space
                direction="vertical"
                size={24}
                style={{display: "flex", marginTop: "24px"}}
              >
                <CustomInputComponent
                  id="password"
                  name="password"
                  type="password"
                  placeholder="New password"
                  error={errors.password?.message}
                  control={control}
                />
                <CustomInputComponent
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="Confirm password"
                  error={errors.confirmPassword?.message}
                  control={control}
                />
              </Space>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "flex-end",
                  marginTop: "36px",
                  minHeight: "56px",
                }}
              >
                <Button
                  htmlType="submit"
                  disabled={isConfirmPasswordDisabled}
                  loading={confirmPasswordMutation.isLoading}
                  style={{
                    borderRadius: 14,
                    ...(isConfirmPasswordDisabled
                      ? {
                          backgroundColor: "#F5F5F5",
                          borderColor: "#D9D9D9",
                          color: "#BFBFBF",
                        }
                      : {
                          backgroundColor: "#1C1726",
                          borderColor: "#1C1726",
                          color: "#fff",
                        }),
                  }}
                >
                  Save changes
                </Button>
              </div>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}
